import React, { useState } from 'react';
import apiRequest from '../services/api';

const DeleteData = () => {
  const [formData, setFormData] = useState({
    identifier: '',
    username: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback(null);
    setIsSubmitting(true);

    try {
      const { data } = await apiRequest('/web/account-deletion-request/', {
        method: 'POST',
        body: {
          title: 'Account deletion request (web form) – CheckStall',
          email_or_phone: formData.identifier,
          username: formData.username
        }
      });

      if (data && data.success) {
        setFeedback({
          type: 'success',
          message: data.message || 'Your request has been submitted.'
        });
        setFormData({
          identifier: '',
          username: ''
        });
      } else {
        setFeedback({
          type: 'error',
          message:
            (data && (data.message || data.detail)) ||
            'Something went wrong while submitting your request. Please try again or contact support.'
        });
      }
    } catch (error) {
      const response = error.response;
      const data = response?.data;

      if (response && response.status === 403 && data && data.detail === 'Request not allowed from this origin.') {
        setFeedback({
          type: 'error',
          message:
            'This request is not allowed from this website. Please submit the request from an allowed domain or use the Checkstall app.'
        });
      } else if (data && (data.message || data.detail)) {
        setFeedback({
          type: 'error',
          message: data.message || data.detail
        });
      } else {
        setFeedback({
          type: 'error',
          message: 'Unable to reach the server. Please check your internet connection and try again.'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-gradient-to-br from-primary-50 to-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Delete Account and Data
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              At Checkstall, you stay in control of your data. If you want to delete your account
              and personal data, you can use any of the options below.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="bg-white rounded-2xl shadow-soft p-8 space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Option 1: In-app account deletion</h2>
            <p className="text-gray-700">
              If you have access to your Checkstall account, you can submit a deletion request
              directly from the app:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              <li>Log in to your Checkstall app.</li>
              <li>Go to <span className="font-semibold">Account Settings</span>.</li>
              <li>Select <span className="font-semibold">Request Account Delete</span>.</li>
            </ol>
            <p className="text-gray-700">
              Once you submit the request in the app, your account and personal data will be deleted
              within <span className="font-semibold">30 days</span>, in line with our data
              protection policies.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-soft p-8 space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Option 2: Request via form</h2>
            <p className="text-gray-700">
              If you cannot access your account or prefer not to use the app, you can request
              account and data deletion using the form below. We will review your request and
              complete the deletion within <span className="font-semibold">30 days</span>.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="identifier"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Registered email address or phone number *
                </label>
                <input
                  type="text"
                  id="identifier"
                  name="identifier"
                  required
                  value={formData.identifier}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-primary focus:border-transparent transition-colors duration-200"
                  placeholder="Enter the email or phone number linked to your account"
                />
              </div>

              <div>
                <label
                  htmlFor="username"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Username *
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  required
                  value={formData.username}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-primary focus:border-transparent transition-colors duration-200"
                  placeholder="Enter your Checkstall username"
                />
              </div>

              <p className="text-sm text-gray-600">
                By submitting this form, you are requesting that we delete your Checkstall account
                and associated personal data. We may contact you using the details provided to
                verify your identity before completing the deletion.
              </p>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary text-white py-3 px-6 rounded-2xl font-semibold hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit deletion request'}
              </button>

              {feedback && (
                <div
                  className={`mt-4 px-4 py-3 rounded-2xl text-sm ${
                    feedback.type === 'success'
                      ? 'bg-green-50 text-green-700'
                      : 'bg-red-50 text-red-700'
                  }`}
                >
                  {feedback.message}
                </div>
              )}
            </form>
          </div>

          <div className="bg-white rounded-2xl shadow-soft p-8 space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">
              Account Deletion and Data Retention
            </h2>
            <p className="text-gray-700">
              When you request account deletion, we permanently delete the following types of
              personal data in line with our data protection obligations and platform requirements:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li>Personal profile information (name, email address, phone number).</li>
              <li>Account login credentials.</li>
              <li>Saved preferences and user-generated content.</li>
            </ul>
            <p className="text-gray-700">
              However, certain data may be retained for legal, regulatory, or operational purposes,
              including:
            </p>
            <ol className="list-decimal list-inside space-y-1 text-gray-700">
              <li>
                Transaction, order, and financial records, which may be retained for up to{' '}
                <span className="font-semibold">7 years</span> to comply with applicable laws.
              </li>
              <li>
                Security and fraud prevention logs retained for up to{' '}
                <span className="font-semibold">90 days</span>.
              </li>
              <li>
                Any retained data will be securely stored and used only where legally required.
              </li>
            </ol>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DeleteData;
